import { useEffect, useState } from "react";
import { getEvents } from "@/lib/api";
import PullQuote from "@/components/PullQuote";
import { Calendar, MapPin, Clock, Gamepad2, Facebook } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import Seo from "@/components/Seo";
import { PAGE_META, eventSchema } from "@/lib/seo";
import { QUOTES } from "@/data/site";

export default function Events() {
  const { openBooking } = useBooking();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEvents()
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="events-page">
      <Seo
        {...PAGE_META.events}
        schema={events.map((ev) => eventSchema(ev))}
      />
      <section className="relative py-24 bg-gradient-to-br from-[#280000] to-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h1 className="font-display font-extrabold italic uppercase fluid-h1">
            <span className="text-rgv-red">Events</span> & Tournaments
          </h1>
          <p className="mt-4 text-rgv-gray text-lg max-w-2xl">
            Tournament dates, community game nights and more — right here in the RGV.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        {loading ? (
          <p className="text-rgv-gray">Loading events...</p>
        ) : events.length === 0 ? (
          <div data-testid="events-empty" className="text-center border border-rgv-maroon rounded-2xl bg-[#111] p-12">
            <Gamepad2 className="h-12 w-12 text-rgv-red mx-auto mb-4" />
            <h3 className="font-display uppercase italic text-3xl mb-3">No Upcoming Events Yet</h3>
            <p className="text-rgv-gray max-w-md mx-auto">
              We&rsquo;re lining up our next tournaments and game nights. Follow us on Facebook or reach
              out to get on the list.
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="events-facebook-btn"
                className="btn-skew btn-outline-red px-6 py-3"
              >
                <span className="flex items-center gap-2"><Facebook className="h-5 w-5" /> Follow On Facebook</span>
              </a>
              <button onClick={() => openBooking()} data-testid="events-book-btn" className="btn-skew btn-red px-6 py-3">
                <span>Host An Event</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {events.map((ev) => (
              <div
                key={ev.id}
                data-testid={`event-item-${ev.id}`}
                className="hover-glow border border-rgv-maroon rounded-xl bg-[#111] p-6 flex flex-col sm:flex-row sm:items-center gap-4 border-l-4 border-l-rgv-red"
              >
                <div className="flex-1">
                  <h3 className="font-display uppercase italic text-2xl">{ev.title}</h3>
                  {ev.description && <p className="text-rgv-gray mt-1">{ev.description}</p>}
                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-rgv-gray">
                    <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-rgv-red" /> {ev.date}</span>
                    {ev.time && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-rgv-red" /> {ev.time}</span>}
                    {ev.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-rgv-red" /> {ev.location}</span>}
                    {ev.game && <span className="flex items-center gap-1.5"><Gamepad2 className="h-4 w-4 text-rgv-red" /> {ev.game}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <PullQuote quotes={QUOTES.events} />
      </section>
    </div>
  );
}
